import { Component, OnInit } from '@angular/core';
import { environment } from '../../environments/environment';

interface WidgetConfig {
  id: string;
  title: string;
  width: 'full' | 'half';
  visible: boolean;
  order: number;
  collapsed: boolean;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {

  enableInspectionReports = environment.inspectionReports;
  userData: any;
  inspector = false;
  widgets: WidgetConfig[] = [];
  dragWidgetId: string = null;
  dropTargetId: string = null;
  isDragActive = false;

  // Dashboard settings (same as original dashboard)
  card = {
    heading1: true,
    heading2: true,
    heading3: false,
    heading4: true,
    heading5: false,
    heading6: false,
    headingpayroll2: true,
    headingWidget: true,
    wantsEarlier: true,
    inspectIncomplete: true,
    inspectNeedPayment: true,
    inspectReadySend: true,
    onlineScheduling: true,
    agentOutreach: true
  };
  head = false;

  dateRange = {
    job_cost: 0,
    inspection_scheduled: 0,
    payroll: 0,
    payroll2: 0,
    estimated: 0
  };

  private storageKey = 'home_layout';

  private defaultWidgets: WidgetConfig[] = [
    { id: 'quick-widget', title: 'Quick Report Widgets', width: 'full', visible: true, order: 0, collapsed: false },
    { id: 'wants-earlier', title: 'Wants Earlier', width: 'full', visible: true, order: 1, collapsed: false },
    { id: 'inspect-incomplete', title: 'Inspections: Incomplete', width: 'full', visible: true, order: 2, collapsed: false },
    { id: 'inspect-need-payment', title: 'Inspections: Need Payment / Proofed', width: 'full', visible: true, order: 3, collapsed: false },
    { id: 'inspect-ready-send', title: 'Inspections: Ready to Send', width: 'full', visible: true, order: 4, collapsed: false },
    { id: 'online-scheduling', title: 'Online Schedule Pending Acceptance', width: 'full', visible: true, order: 5, collapsed: false },
    { id: 'agent-outreach', title: 'Agent Outreach', width: 'full', visible: true, order: 6, collapsed: false },
    { id: 'jobcosting', title: 'Inspector Job Costing', width: 'full', visible: true, order: 7, collapsed: false },
    { id: 'inspections-scheduled', title: 'Inspections Scheduled Report', width: 'full', visible: true, order: 8, collapsed: false },
    { id: 'payroll', title: 'Inspector Payroll (Payment)', width: 'full', visible: true, order: 9, collapsed: false },
    { id: 'payroll-charge', title: 'Inspector Payroll (Charge)', width: 'full', visible: true, order: 10, collapsed: false },
    { id: 'estimated-actual', title: 'Inspector Estimated v Actual', width: 'full', visible: true, order: 11, collapsed: false }
  ];

  ngOnInit() {
    this.userData = JSON.parse(localStorage.getItem('user'));
    this.inspector = this.userData && this.userData.user_level !== 'inspector';
    this.loadLayout();
    this.loadDashboardSettings();
  }

  // Dashboard settings methods (same as original dashboard)
  loadDashboardSettings() {
    if (localStorage.getItem('card') != null) {
      this.card = JSON.parse(localStorage.getItem('card'));
    }
    if (localStorage.getItem('date_range') != null) {
      this.dateRange = JSON.parse(localStorage.getItem('date_range'));
    }
  }

  toggleHead() {
    this.head = true;
  }

  toggleCard() {
    this.head = false;
    localStorage.setItem('card', JSON.stringify(this.card));
    localStorage.setItem('date_range', JSON.stringify(this.dateRange));
  }

  jobdateRange(date) {
    this.dateRange.job_cost = date;
  }

  inspectiondateRange(date) {
    this.dateRange.inspection_scheduled = date;
  }

  payrolldateRange(date) {
    this.dateRange.payroll = date;
  }

  payrolldateRange2(date) {
    this.dateRange.payroll2 = date;
  }

  estimateddateRange(date) {
    this.dateRange.estimated = date;
  }

  // Widget layout methods
  loadLayout() {
    const saved = localStorage.getItem(this.getStorageKey());
    if (saved) {
      try {
        const savedWidgets: WidgetConfig[] = JSON.parse(saved);
        // Add collapsed field if missing from older saved data
        savedWidgets.forEach(w => {
          if (w.collapsed === undefined) { w.collapsed = false; }
        });
        const savedIds = savedWidgets.map(w => w.id);
        const newWidgets = this.defaultWidgets.filter(w => !savedIds.includes(w.id));
        this.widgets = [...savedWidgets, ...newWidgets];
      } catch (e) {
        this.widgets = this.defaultWidgets.map(w => ({ ...w }));
      }
    } else {
      this.widgets = this.defaultWidgets.map(w => ({ ...w }));
    }
  }

  private getStorageKey(): string {
    const userId = this.userData ? this.userData.id : 'default';
    return this.storageKey + '_' + userId;
  }

  saveLayout() {
    localStorage.setItem(this.getStorageKey(), JSON.stringify(this.widgets));
  }

  setWidth(widget: WidgetConfig, width: 'full' | 'half') {
    widget.width = width;
    this.saveLayout();
  }

  toggleVisibility(widget: WidgetConfig) {
    widget.visible = !widget.visible;
    this.saveLayout();
  }

  toggleCollapse(widget: WidgetConfig) {
    widget.collapsed = !widget.collapsed;
    this.saveLayout();
  }

  // Swap two adjacent half-width widgets
  swapWithPartner(widget: WidgetConfig) {
    const visible = this.visibleWidgets;
    const visIndex = visible.findIndex(w => w.id === widget.id);
    if (visIndex === -1) return;

    let partnerId: string = null;
    // Find the adjacent half-width partner
    if (visIndex > 0 && visible[visIndex - 1].width === 'half') {
      partnerId = visible[visIndex - 1].id;
    } else if (visIndex < visible.length - 1 && visible[visIndex + 1].width === 'half') {
      partnerId = visible[visIndex + 1].id;
    }
    if (!partnerId) return;

    const idxA = this.widgets.findIndex(w => w.id === widget.id);
    const idxB = this.widgets.findIndex(w => w.id === partnerId);
    if (idxA === -1 || idxB === -1) return;

    // Swap in the main array
    const temp = this.widgets[idxA];
    this.widgets[idxA] = this.widgets[idxB];
    this.widgets[idxB] = temp;

    this.widgets.forEach((w, i) => w.order = i);
    this.saveLayout();
  }

  // Check if widget has an adjacent half-width partner to swap with
  hasSwapPartner(widget: WidgetConfig): boolean {
    if (widget.width !== 'half') return false;
    const visible = this.visibleWidgets;
    const idx = visible.findIndex(w => w.id === widget.id);
    if (idx === -1) return false;
    if (idx > 0 && visible[idx - 1].width === 'half') return true;
    if (idx < visible.length - 1 && visible[idx + 1].width === 'half') return true;
    return false;
  }

  // Drag and drop handlers
  onDragStart(event: DragEvent, widget: WidgetConfig) {
    this.dragWidgetId = widget.id;
    this.isDragActive = true;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', widget.id);
  }

  onDragOver(event: DragEvent, targetId: string) {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';
    if (this.dragWidgetId && this.dragWidgetId !== targetId) {
      this.dropTargetId = targetId;
    }
  }

  onDragLeave(event: DragEvent, targetId: string) {
    const el = event.currentTarget as HTMLElement;
    const related = event.relatedTarget as HTMLElement;
    if (!related || !el.contains(related)) {
      if (this.dropTargetId === targetId) {
        this.dropTargetId = null;
      }
    }
  }

  onDrop(event: DragEvent, targetId: string) {
    event.preventDefault();
    event.stopPropagation();
    this.dropTargetId = null;
    this.isDragActive = false;

    if (!this.dragWidgetId || this.dragWidgetId === targetId) return;

    const fromIndex = this.widgets.findIndex(w => w.id === this.dragWidgetId);
    const toIndex = this.widgets.findIndex(w => w.id === targetId);
    if (fromIndex === -1 || toIndex === -1) return;

    const moved = this.widgets.splice(fromIndex, 1)[0];
    this.widgets.splice(toIndex, 0, moved);

    this.widgets.forEach((w, i) => w.order = i);
    this.dragWidgetId = null;
    this.saveLayout();
  }

  onDragEnd(event: DragEvent) {
    this.dragWidgetId = null;
    this.dropTargetId = null;
    this.isDragActive = false;
  }

  isDragging(widget: WidgetConfig): boolean {
    return this.dragWidgetId === widget.id;
  }

  isDropTarget(id: string): boolean {
    return this.dropTargetId === id;
  }

  resetLayout() {
    this.widgets = this.defaultWidgets.map(w => ({ ...w }));
    this.saveLayout();
  }

  get visibleWidgets(): WidgetConfig[] {
    return this.widgets.filter(w => w.visible);
  }

  // Check if a half-width widget is alone (no adjacent half-width partner on either side)
  // Used to show a filler drop zone next to it during drag
  isLoneHalf(widget: WidgetConfig, index: number): boolean {
    if (widget.width !== 'half') return false;
    const visible = this.visibleWidgets;
    const prev = index > 0 ? visible[index - 1] : null;
    const next = index < visible.length - 1 ? visible[index + 1] : null;

    // Count how many consecutive halfs end at this widget
    // A half is "alone" if neither neighbor is also half
    // But we also need to handle pairs: in a pair the second one is not alone
    // Walk backwards to find the start of this half-group
    let groupStart = index;
    while (groupStart > 0 && visible[groupStart - 1].width === 'half') {
      groupStart--;
    }
    const posInGroup = index - groupStart;
    // If this widget is at an odd position in the group (0-indexed), it's the right side of a pair
    // If it's at an even position, check if there's a next half
    if (posInGroup % 2 === 0) {
      // Left side - alone if next is not half
      return !next || next.width !== 'half';
    }
    return false;
  }
}
